// all dates are in UTC, don't put any timezone concerns in this file!

/**
 *  A shiftable is something that can be on on-call shifts, such as a team member.
 * The scheduler is fully on-call agnostic and could be used to schedule anything
 * that checks the Shiftable interface.
 */
export type Shiftable = {
  id: string;
  forcedDates?: string[]; // YYYY-MM-DD
  excludedDates?: string[]; // YYYY-MM-DD
};

const debug = false;

type RotationType = "weekly" | "daily";

type SortOrder<T extends Shiftable> =
  | { type: "id" }
  | { type: "custom"; fn: (a: T, b: T) => number };

type Shift = {
  shiftableId: string;
  startDate: string; // YYYY-MM-DD
  endDate: string; // YYYY-MM-DD
};

export type ScheduleOptions<T extends Shiftable> = {
  rotationType: RotationType;
  sortOrder: SortOrder<T>;
  startDate: string; // YYYY-MM-DD
};

export type ViewOptions =
  | {
      fromDate: string; // YYYY-MM-DD
      toDate: string; // YYYY-MM-DD
    }
  | {
      fromDate: string; // YYYY-MM-DD
      shiftsCount: number; // number of shifts to generate
    };

/**
 * Custom sort always takes precedence over ID sort.
 */
function sortShiftables<T extends Shiftable>(
  shiftables: T[],
  sortOrder: SortOrder<T>,
): T[] {
  const sortedShiftables = [...shiftables].sort((a, b) => {
    if (sortOrder.type === "custom") {
      const customResult = sortOrder.fn(a, b);
      if (customResult === 0) {
        return a.id.localeCompare(b.id);
      }
      return customResult;
    }
    return a.id.localeCompare(b.id);
  });

  // sort excluded and forced dates for every shiftable for deterministic results
  for (const shiftable of sortedShiftables) {
    if (shiftable.forcedDates) {
      shiftable.forcedDates.sort((a, b) => a.localeCompare(b));
    }
    if (shiftable.excludedDates) {
      shiftable.excludedDates.sort((a, b) => a.localeCompare(b));
    }
  }

  return sortedShiftables;
}

function formatDate(date: Date): string {
  return date.toISOString().split("T")[0]!;
}

function validateDate(date: string, fieldName: string): void {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    throw new Error(
      `invalid ${fieldName} format: ${date}, expected YYYY-MM-DD`,
    );
  }
  if (isNaN(new Date(date).getTime())) {
    throw new Error(`invalid ${fieldName}: ${date}`);
  }
}

function validateShiftable(shiftable: Shiftable, index: number): void {
  if (typeof shiftable.id !== "string" || shiftable.id.trim() === "") {
    throw new Error(
      `shiftable at index ${index} has an invalid id: ${shiftable.id}`,
    );
  }

  shiftable.forcedDates?.forEach((date) => validateDate(date, "forcedDate"));
  shiftable.excludedDates?.forEach((date) =>
    validateDate(date, "excludedDate"),
  );

  const conflicts = shiftable.forcedDates?.filter((date) =>
    shiftable.excludedDates?.includes(date),
  );
  if (conflicts?.length) {
    throw new Error(
      `shiftable ${shiftable.id} has dates that are both forced and excluded: ${conflicts.join(", ")}`,
    );
  }
}

/**
 * Return a list of shifts for a given set of shiftables, schedule options and view options.
 *
 * A forced date overrides whoever was scheduled for that date. The future shifts from this point
 * continue to be scheduled as usual.
 *
 * An excluded date makes sure that the shiftable is not scheduled for that date. The future
 * available shift is scheduled.
 *
 * It's assumed that the provided forced and excluded dates can be scheduled into sensible shifts for the shiftables provided.
 *
 * This function is deterministic and will always return the same result for the same input.
 *
 * The scheduler will try to avoid the same person being scheduled for consecutive shifts, but will
 * not always succeed depending on the excluded and forced dates.
 *
 * Any scheduling logic more complex than this should probably be implemented using an existing constraint solver.
 */
export function renderShifts<T extends Shiftable>(
  shiftables: T[],
  options: ScheduleOptions<T>,
  viewOptions: ViewOptions,
): Shift[] {
  debug && console.log("\n=== Starting renderShifts ===");
  debug && console.log("Input:", { shiftables, options, viewOptions });

  if (!Array.isArray(shiftables) || shiftables.length === 0) {
    throw new Error("shiftables must be a non-empty array");
  }

  shiftables.forEach(validateShiftable);
  validateDate(options.startDate, "startDate");

  let viewFromDate: Date;
  let viewToDate: Date;

  if ("fromDate" in viewOptions && "toDate" in viewOptions) {
    viewFromDate = new Date(viewOptions.fromDate);
    viewToDate = new Date(viewOptions.toDate);
    debug && console.log("View range:", { viewFromDate, viewToDate });

    if (isNaN(viewFromDate.getTime())) {
      throw new Error(`invalid fromDate: ${viewOptions.fromDate}`);
    }
    if (isNaN(viewToDate.getTime())) {
      throw new Error(`invalid toDate: ${viewOptions.toDate}`);
    }
    if (viewFromDate >= viewToDate) {
      throw new Error(
        `fromDate (${viewOptions.fromDate}) must be before toDate (${viewOptions.toDate})`,
      );
    }
    if (viewFromDate < new Date(options.startDate)) {
      throw new Error(
        `fromDate (${viewOptions.fromDate}) must be after or equal to startDate (${options.startDate})`,
      );
    }
  } else if ("fromDate" in viewOptions && "shiftsCount" in viewOptions) {
    const fromDate = new Date(viewOptions.fromDate);
    if (isNaN(fromDate.getTime())) {
      throw new Error(`invalid fromDate: ${viewOptions.fromDate}`);
    }
    if (fromDate < new Date(options.startDate)) {
      throw new Error(
        `fromDate (${viewOptions.fromDate}) must be after or equal to startDate (${options.startDate})`,
      );
    }
    if (
      typeof viewOptions.shiftsCount !== "number" ||
      viewOptions.shiftsCount <= 0
    ) {
      throw new Error(
        `shiftsCount must be a positive number, got: ${viewOptions.shiftsCount}`,
      );
    }

    viewFromDate = fromDate;
    const dayMs = 24 * 60 * 60 * 1000;
    const weekMs = 7 * dayMs;
    const intervalMs = options.rotationType === "weekly" ? weekMs : dayMs;
    viewToDate = new Date(
      viewFromDate.getTime() + viewOptions.shiftsCount * intervalMs,
    );
    debug && console.log("View range:", { viewFromDate, viewToDate });
  } else {
    throw new Error(
      "invalid viewOptions: must contain either 'fromDate' and 'toDate' or 'fromDate' and 'shiftsCount'",
    );
  }

  const sortedShiftables = sortShiftables(shiftables, options.sortOrder);
  debug && console.log("Sorted shiftables:", sortedShiftables);

  const shifts: Shift[] = [];
  let currentDate = new Date(options.startDate);
  debug && console.log("Starting from date:", currentDate);

  function getShiftIndex(date: Date): number {
    const startDate = new Date(options.startDate);
    const currentDate = new Date(date);

    // set both dates to midnight UTC to avoid timezone issues
    startDate.setUTCHours(0, 0, 0, 0);
    currentDate.setUTCHours(0, 0, 0, 0);

    const dayMs = 24 * 60 * 60 * 1000;

    // calculate the difference in days
    const diffDays = Math.floor(
      (currentDate.getTime() - startDate.getTime()) / dayMs,
    );
    debug &&
      console.log("Shift index calculation:", {
        startDate,
        currentDate,
        diffDays,
        weekNumber: Math.floor(diffDays / 7),
        isWeekly: options.rotationType === "weekly",
      });

    // ror weekly rotations, we want to count complete weeks
    if (options.rotationType === "weekly") {
      return Math.floor(diffDays / 7);
    }

    return diffDays;
  }

  while (currentDate < viewToDate) {
    debug && console.log("\n=== Processing date:", currentDate, "===");
    const shiftDuration = options.rotationType === "weekly" ? 7 : 1;
    const shiftEndDate = new Date(currentDate);
    shiftEndDate.setDate(shiftEndDate.getDate() + shiftDuration);

    const currentDateString = formatDate(currentDate);
    const shiftIndex = getShiftIndex(currentDate);
    debug && console.log("Initial shift index:", shiftIndex);
    let assignedShiftable =
      sortedShiftables[shiftIndex % sortedShiftables.length];
    debug && console.log("Initial assigned shiftable:", assignedShiftable);

    const forcedShiftable = sortedShiftables.find((s) =>
      s.forcedDates?.includes(currentDateString),
    );
    if (forcedShiftable) {
      debug && console.log("Found forced shiftable:", forcedShiftable);
      assignedShiftable = forcedShiftable;
    } else {
      let checkedCount = 0;
      let foundNonConsecutive = false;

      // first try: find someone who is not excluded and not consecutive
      debug &&
        console.log(
          "Trying to find non-consecutive and non-excluded shiftable...",
        );
      while (checkedCount < sortedShiftables.length && !foundNonConsecutive) {
        const candidateIndex =
          (shiftIndex + checkedCount) % sortedShiftables.length;
        assignedShiftable = sortedShiftables[candidateIndex];
        debug &&
          console.log("Checking candidate:", {
            checkedCount,
            candidateIndex,
            candidateId: assignedShiftable?.id,
            isExcluded:
              assignedShiftable?.excludedDates?.includes(currentDateString),
            isConsecutive:
              shifts.length > 0 &&
              shifts[shifts.length - 1]?.shiftableId === assignedShiftable?.id,
          });
        checkedCount++;

        // skip if excluded
        if (assignedShiftable?.excludedDates?.includes(currentDateString)) {
          debug &&
            console.log("Skipping excluded shiftable:", assignedShiftable.id);
          continue;
        }

        // skip if consecutive (unless we've tried everyone)
        if (
          shifts.length > 0 &&
          shifts[shifts.length - 1]?.shiftableId === assignedShiftable?.id
        ) {
          debug &&
            console.log(
              "Skipping consecutive shiftable:",
              assignedShiftable?.id,
            );
          continue;
        }

        foundNonConsecutive = true;
      }

      // second try: find someone who is not excluded (allowing consecutive shifts)
      if (!foundNonConsecutive) {
        debug &&
          console.log(
            "No non-consecutive shiftable found, trying to find non-excluded shiftable...",
          );
        checkedCount = 0;
        let foundNonExcluded = false;

        while (checkedCount < sortedShiftables.length && !foundNonExcluded) {
          const candidateIndex =
            (shiftIndex + checkedCount) % sortedShiftables.length;
          assignedShiftable = sortedShiftables[candidateIndex];
          debug &&
            console.log("Checking candidate (allowing consecutive):", {
              checkedCount,
              candidateIndex,
              candidateId: assignedShiftable?.id,
              isExcluded:
                assignedShiftable?.excludedDates?.includes(currentDateString),
            });
          checkedCount++;

          if (!assignedShiftable?.excludedDates?.includes(currentDateString)) {
            foundNonExcluded = true;
          }
        }

        if (!foundNonExcluded) {
          throw new Error(
            `no available shiftable found for date ${currentDateString}`,
          );
        }
      }
    }

    if (!assignedShiftable) {
      debug && console.log("no assigned shiftable found");
      continue;
    }

    debug && console.log("Final assigned shiftable:", assignedShiftable);
    shifts.push({
      shiftableId: assignedShiftable.id,
      startDate: currentDateString,
      endDate: formatDate(shiftEndDate),
    });

    currentDate = shiftEndDate;
  }

  const filteredShifts = shifts
    .filter((shift) => new Date(shift.endDate) > viewFromDate)
    .slice(
      0,
      "shiftsCount" in viewOptions ? viewOptions.shiftsCount : undefined,
    );

  debug && console.log("\n=== Final shifts ===");
  debug && console.log(JSON.stringify(filteredShifts, null, 2));

  return filteredShifts;
}
